import { useCallback, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { tokenRequest } from '@/config/msalConfig';
import { acquireToken } from '@/auth/acquireToken';

/**
 * Hook to handle Graph consent and acquire API access tokens.
 *
 * Two methods:
 * - ``acquireApiToken()`` — silently acquires an API token for backend calls
 *   (profile, rate-limit, reports). No Graph consent popup. This is all the
 *   default form-only report (Mode A) needs.
 * - ``acquireApiTokenWithConsent(graphScopes)`` — ensures the given Graph
 *   scopes are consented (popup if needed) then acquires the API token. Used
 *   when starting an analysis that needs OBO Graph access (Mode B). Pass the
 *   SharePoint scopes from ``graphSharePointScopes``.
 */
export function useGraphConsent() {
  const { instance } = useMsal();
  const [isConsenting, setIsConsenting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Acquire an API-scoped token silently (no Graph consent).
   * For general backend calls: profile, rate-limit, reports.
   */
  const acquireApiToken = useCallback(async (): Promise<string | null> => {
    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) return null;

    try {
      // Robust acquisition: silent refresh, then a GUARDED interactive redirect
      // on interaction-required (SPA-Auth Standard rules 2 & 4). Replaces the
      // previous silent→popup→null path that dead-ended on an expired session.
      return await acquireToken(instance, { ...tokenRequest, account: activeAccount });
    } catch {
      return null;
    }
  }, [instance]);

  /**
   * Trigger Graph consent (popup if needed) then acquire an API token.
   * For starting analyses that need OBO Graph access on the backend.
   */
  const acquireApiTokenWithConsent = useCallback(async (graphScopes: string[]): Promise<string | null> => {
    setIsConsenting(true);
    setError(null);

    const activeAccount = instance.getActiveAccount();
    if (!activeAccount) {
      setError('No active account. Please sign in first.');
      setIsConsenting(false);
      return null;
    }

    try {
      // SPA-Auth Standard rule 2 — initialize() before any MSAL token call
      // (idempotent; guards against an effect racing MSAL's own init).
      await instance.initialize();

      // Step 1: Ensure the requested Graph permissions are consented (may show popup)
      try {
        await instance.acquireTokenSilent({
          scopes: graphScopes,
          account: activeAccount,
        });
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          await instance.acquireTokenPopup({
            scopes: graphScopes,
            account: activeAccount,
          });
        } else {
          throw silentError;
        }
      }

      // Step 2: Acquire the API access token (this is what we send to the backend)
      try {
        const apiResponse = await instance.acquireTokenSilent({
          ...tokenRequest,
          account: activeAccount,
        });
        setIsConsenting(false);
        return apiResponse.accessToken;
      } catch (silentError) {
        if (silentError instanceof InteractionRequiredAuthError) {
          const apiResponse = await instance.acquireTokenPopup({
            ...tokenRequest,
            account: activeAccount,
          });
          setIsConsenting(false);
          return apiResponse.accessToken;
        }
        throw silentError;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Token acquisition failed';
      setError(msg);
      setIsConsenting(false);
      return null;
    }
  }, [instance]);

  return { acquireApiToken, acquireApiTokenWithConsent, isConsenting, error };
}
